import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { X, Plus, Trash2, Edit2, Check, Target, Layers } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { waGroupsAPI } from "../services/api";

const GroupSettingsModal = ({ isOpen, onClose, clienteId, metaGeral, onMetaGeralChange }) => {
  const { darkMode } = useTheme();
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newCatLabel, setNewCatLabel] = useState("");
  const [newCatMultiplo, setNewCatMultiplo] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [editCatLabel, setEditCatLabel] = useState("");
  const [newOpcaoMap, setNewOpcaoMap] = useState({});
  const [metaGeralLocal, setMetaGeralLocal] = useState(metaGeral || 0);

  useEffect(() => {
    if (isOpen) loadCategorias();
  }, [isOpen]);

  useEffect(() => {
    setMetaGeralLocal(metaGeral || 0);
  }, [metaGeral]);

  const loadCategorias = async () => {
    setLoading(true);
    try {
      const res = await waGroupsAPI.getCategorias();
      setCategorias(res.data?.data || []);
    } catch (e) {
      toast.error("Erro ao carregar categorias");
    }
    setLoading(false);
  };

  const handleCreateCategoria = async () => {
    if (!newCatLabel.trim()) return;
    try {
      await waGroupsAPI.createCategoria({ label: newCatLabel.trim(), multiplo: newCatMultiplo });
      setNewCatLabel("");
      setNewCatMultiplo(false);
      loadCategorias();
      toast.success("Categoria criada");
    } catch (e) {
      toast.error("Erro ao criar categoria");
    }
  };

  const handleUpdateCategoria = async (id) => {
    if (!editCatLabel.trim()) return;
    try {
      await waGroupsAPI.updateCategoria(id, { label: editCatLabel.trim() });
      setEditingCat(null);
      loadCategorias();
    } catch (e) {
      toast.error("Erro ao editar categoria");
    }
  };

  const handleDeleteCategoria = async (id) => {
    if (!window.confirm("Deletar essa categoria e todas suas opções?")) return;
    try {
      await waGroupsAPI.deleteCategoria(id);
      loadCategorias();
      toast.success("Categoria removida");
    } catch (e) {
      toast.error("Erro ao remover");
    }
  };

  const handleCreateOpcao = async (categoriaId) => {
    const nome = newOpcaoMap[categoriaId]?.trim();
    if (!nome) return;
    try {
      await waGroupsAPI.createOpcao(categoriaId, { nome });
      setNewOpcaoMap({ ...newOpcaoMap, [categoriaId]: "" });
      loadCategorias();
    } catch (e) {
      toast.error("Erro ao criar opção");
    }
  };

  const handleDeleteOpcao = async (opcaoId) => {
    try {
      await waGroupsAPI.deleteOpcao(opcaoId);
      loadCategorias();
    } catch (e) {
      toast.error("Erro ao remover opção");
    }
  };

  const handleSaveMetaGeral = async () => {
    try {
      const api = (await import("../services/api")).default;
      await api.put('/grupo-categorias/meta-geral', { meta_contatos_unicos: metaGeralLocal });
      onMetaGeralChange?.(metaGeralLocal);
      toast.success("Meta geral salva");
    } catch (e) {
      toast.error("Erro ao salvar meta");
    }
  };

  if (!isOpen) return null;

  const cardBg = darkMode ? "bg-zinc-800/80 border-zinc-700" : "bg-white border-gray-100";
  const modalBg = darkMode ? "bg-zinc-900" : "bg-gray-50";
  const headerBg = darkMode ? "bg-zinc-800" : "bg-white";
  const text = darkMode ? "text-white" : "text-gray-900";
  const textMuted = darkMode ? "text-gray-400" : "text-gray-500";
  const inputCls = darkMode ? "bg-zinc-800 border-zinc-700 text-white placeholder-zinc-500" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400";
  const chipBg = darkMode ? "bg-zinc-700 text-gray-200 hover:bg-zinc-600" : "bg-gray-100 text-gray-700 hover:bg-gray-200";
  const chipDelete = "ml-1 opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-opacity";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className={`${modalBg} rounded-2xl shadow-2xl w-full max-w-xl max-h-[85vh] overflow-hidden flex flex-col border ${darkMode ? 'border-zinc-700' : 'border-gray-200'}`} onClick={e => e.stopPropagation()}>
        
        {/* Header */}
        <div className={`${headerBg} px-6 py-5 border-b ${darkMode ? 'border-zinc-700' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div>
              <h2 className={`text-base font-semibold ${text}`}>Configuração de Grupos</h2>
              <p className={`text-xs mt-0.5 ${textMuted}`}>Gerencie categorias e metas dos seus grupos</p>
            </div>
            <button onClick={onClose} className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-zinc-700' : 'hover:bg-gray-100'}`}>
              <X size={18} className={textMuted} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          
          {/* Meta Geral */}
          <div className={`rounded-xl border p-4 ${cardBg}`}>
            <div className="flex items-center gap-2 mb-3">
              <Target size={15} className="text-blue-500" />
              <span className={`text-sm font-medium ${text}`}>Meta de Contatos Únicos</span>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="number"
                value={metaGeralLocal}
                onChange={(e) => setMetaGeralLocal(Number(e.target.value))}
                className={`${inputCls} border rounded-lg px-3 py-2 w-28 text-sm focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 outline-none transition`}
                placeholder="5000"
              />
              <button
                onClick={handleSaveMetaGeral}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                Salvar
              </button>
              <span className={`text-xs ${textMuted}`}>participantes únicos desejado</span>
            </div>
          </div>

          {/* Categorias */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Layers size={15} className="text-purple-500" />
              <span className={`text-sm font-medium ${text}`}>Categorias</span>
              <span className={`text-xs ${textMuted}`}>({categorias.length})</span>
            </div>

            {/* Nova categoria */}
            <div className={`flex items-center gap-2 mb-5 p-3 rounded-xl border ${cardBg}`}>
              <input
                value={newCatLabel}
                onChange={(e) => setNewCatLabel(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleCreateCategoria()}
                placeholder="Nome da nova categoria..."
                className={`${inputCls} border rounded-lg px-3 py-2 flex-1 text-sm focus:ring-2 focus:ring-green-500/30 focus:border-green-500 outline-none transition`}
              />
              <label className={`flex items-center gap-1.5 text-xs ${textMuted} cursor-pointer select-none whitespace-nowrap`}>
                <input 
                  type="checkbox" 
                  checked={newCatMultiplo} 
                  onChange={(e) => setNewCatMultiplo(e.target.checked)} 
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-3.5 h-3.5" 
                />
                Múltiplo
              </label>
              <button
                onClick={handleCreateCategoria}
                disabled={!newCatLabel.trim()}
                className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
              >
                <Plus size={14} />
              </button>
            </div>

            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full" />
              </div>
            ) : categorias.length === 0 ? (
              <p className={`text-center py-8 text-sm ${textMuted}`}>Nenhuma categoria criada ainda</p>
            ) : (
              <div className="space-y-3">
                {categorias.map((cat) => (
                  <div key={cat.id} className={`rounded-xl border p-4 ${cardBg} transition-all`}>
                    {/* Header */}
                    <div className="flex items-center justify-between mb-3">
                      {editingCat === cat.id ? (
                        <div className="flex items-center gap-2 flex-1">
                          <input
                            value={editCatLabel}
                            onChange={(e) => setEditCatLabel(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleUpdateCategoria(cat.id)}
                            className={`${inputCls} border rounded-lg px-2.5 py-1.5 text-sm flex-1 outline-none`}
                            autoFocus
                          />
                          <button onClick={() => handleUpdateCategoria(cat.id)} className="p-1.5 rounded-lg bg-green-100 text-green-600 hover:bg-green-200">
                            <Check size={14} />
                          </button>
                          <button onClick={() => setEditingCat(null)} className="p-1.5 rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-zinc-700 dark:hover:bg-zinc-600">
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2">
                            <h4 className={`text-sm font-semibold ${text}`}>{cat.label}</h4>
                            {cat.multiplo && (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 font-medium">
                                múltiplo
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-0.5">
                            <button
                              onClick={async () => {
                                await waGroupsAPI.updateCategoria(cat.id, { multiplo: !cat.multiplo });
                                loadCategorias();
                              }}
                              className={`p-1.5 rounded-lg transition-colors ${darkMode ? 'hover:bg-zinc-700' : 'hover:bg-gray-100'} ${textMuted}`}
                              title={cat.multiplo ? "Desativar múltiplo" : "Ativar múltiplo"}
                            >
                              {cat.multiplo ? '▣' : '▢'}
                            </button>
                            <button
                              onClick={() => { setEditingCat(cat.id); setEditCatLabel(cat.label); }}
                              className={`p-1.5 rounded-lg transition-colors ${darkMode ? 'hover:bg-zinc-700' : 'hover:bg-gray-100'} ${textMuted}`}
                            >
                              <Edit2 size={13} />
                            </button>
                            <button
                              onClick={() => handleDeleteCategoria(cat.id)}
                              className="p-1.5 rounded-lg transition-colors hover:bg-red-50 dark:hover:bg-red-900/20 text-red-400 hover:text-red-600"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Opções como chips */}
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {(cat.opcoes || []).map((opc) => (
                        <span
                          key={opc.id}
                          className={`group inline-flex items-center gap-0.5 px-2.5 py-1 rounded-lg text-xs font-medium ${chipBg} transition-colors cursor-default`}
                        >
                          {opc.nome}
                          <button onClick={() => handleDeleteOpcao(opc.id)} className={chipDelete}>
                            <X size={11} />
                          </button>
                        </span>
                      ))}
                      {(cat.opcoes || []).length === 0 && (
                        <span className={`text-xs italic ${textMuted}`}>Sem opções</span>
                      )}
                    </div>

                    {/* Add opção */}
                    <div className="flex items-center gap-2">
                      <input
                        value={newOpcaoMap[cat.id] || ""}
                        onChange={(e) => setNewOpcaoMap({ ...newOpcaoMap, [cat.id]: e.target.value })}
                        onKeyDown={(e) => e.key === "Enter" && handleCreateOpcao(cat.id)}
                        placeholder="Nova opção..."
                        className={`${inputCls} border rounded-lg px-2.5 py-1.5 text-xs flex-1 outline-none focus:ring-1 focus:ring-blue-500/30 focus:border-blue-500 transition`}
                      />
                      <button
                        onClick={() => handleCreateOpcao(cat.id)}
                        disabled={!newOpcaoMap[cat.id]?.trim()}
                        className="p-1.5 rounded-lg bg-blue-500/10 text-blue-600 hover:bg-blue-500/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupSettingsModal;
